import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  useMarkNotificationRead,
  useNotifications,
} from "@src/packages/care/useNotifications";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  parseBadgeUnlockEvent,
  type BadgeUnlockEventEnvelope,
} from "./badge-events";

type PendingBadgeEvent = {
  event: BadgeUnlockEventEnvelope["event"];
  notificationIds: string[];
};

type BadgeCelebrationContextValue = {
  activeEvent: PendingBadgeEvent | null;
  /**
   * Everything unacknowledged, oldest first.
   *
   * The provider used to hand out only the first, which is why several badges
   * became several dialogs in a row. Exposing the whole set lets one dialog
   * show them together; `activeEvent` stays for the single-badge path and is
   * simply the first of these.
   */
  pendingEvents: PendingBadgeEvent[];
  dismissActive: () => void;
  /** Acknowledge every badge currently pending, in one gesture. */
  dismissAll: () => void;
};

const BadgeCelebrationContext = createContext<BadgeCelebrationContextValue>({
  activeEvent: null,
  pendingEvents: [],
  dismissActive: () => {},
  dismissAll: () => {},
});

export function BadgeCelebrationProvider({
  children,
  isBadgeSupported,
}: {
  children: ReactNode;
  isBadgeSupported: (badgeId: string) => boolean;
}) {
  const { data: notifications = [] } = useNotifications();
  const { patientId } = useApiClient();
  const markRead = useMarkNotificationRead();
  const [dismissedEventIds, setDismissedEventIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    setDismissedEventIds(new Set());
  }, [patientId]);

  const pendingEvents = useMemo(() => {
    const grouped = new Map<string, PendingBadgeEvent>();

    for (const notification of notifications) {
      if (notification.readAt) continue;

      const envelope = parseBadgeUnlockEvent(notification);
      if (
        !envelope ||
        !isBadgeSupported(envelope.event.badgeId) ||
        dismissedEventIds.has(envelope.event.eventId)
      ) {
        continue;
      }

      const existing = grouped.get(envelope.event.eventId);
      if (existing) {
        existing.notificationIds.push(notification.id);
      } else {
        grouped.set(envelope.event.eventId, {
          event: envelope.event,
          notificationIds: [notification.id],
        });
      }
    }

    return [...grouped.values()].sort(
      (a, b) => Date.parse(a.event.occurredAt) - Date.parse(b.event.occurredAt),
    );
  }, [dismissedEventIds, isBadgeSupported, notifications]);

  const dismiss = useCallback(
    (events: PendingBadgeEvent[]) => {
      if (events.length === 0) return;

      setDismissedEventIds((current) => {
        const next = new Set(current);
        for (const pending of events) next.add(pending.event.eventId);
        return next;
      });

      for (const pending of events) {
        for (const notificationId of pending.notificationIds) {
          markRead.mutate(notificationId, {
            onError: () => {
              // Keep a failed acknowledgement available for retry, and only the
              // one that failed: dismissing four badges must not put the other
              // three back because one request lost the network. The server's
              // unread state remains the source of truth across reloads.
              setDismissedEventIds((current) => {
                const next = new Set(current);
                next.delete(pending.event.eventId);
                return next;
              });
            },
          });
        }
      }
    },
    [markRead],
  );

  const dismissActive = useCallback(() => {
    const active = pendingEvents[0];
    if (active) dismiss([active]);
  }, [dismiss, pendingEvents]);

  const dismissAll = useCallback(() => {
    dismiss(pendingEvents);
  }, [dismiss, pendingEvents]);

  const value = useMemo(
    () => ({
      activeEvent: pendingEvents[0] ?? null,
      pendingEvents,
      dismissActive,
      dismissAll,
    }),
    [dismissActive, dismissAll, pendingEvents],
  );

  return (
    <BadgeCelebrationContext.Provider value={value}>
      {children}
    </BadgeCelebrationContext.Provider>
  );
}

export function useBadgeCelebration() {
  return useContext(BadgeCelebrationContext);
}
